interface DeepgramResult {
    is_final?: boolean;
    channel?: {
        alternatives?: Array<{
            transcript?: string;
        }>;
    };
}

export function useSpeechRecognition() {
    const isRecording = ref(false);
    const isTranscribing = ref(false);
    const error = ref<string | null>(null);
    const liveTranscript = ref("");

    let mediaRecorder: MediaRecorder | null = null;
    let audioStream: MediaStream | null = null;
    let ws: WebSocket | null = null;
    let finalizedText = "";
    let cleanupDocListeners: (() => void) | null = null;

    // Called from the component when mouseup/touchend or keyup happens
    let onStopCallback: (() => void) | null = null;

    function registerStopCallback(cb: () => void) {
        onStopCallback = cb;
    }

    // Keyboard shortcut state
    let keyboardCleanup: (() => void) | null = null;

    /**
     * Set up a toggle-to-talk keyboard shortcut.
     * Press Alt+V once to start recording, press again to stop.
     * @param canStart - function that returns true if recording is allowed to start
     * @param onStart - called when recording should start (e.g., save text state)
     */
    function registerKeyboardShortcut(
        canStart: () => boolean,
        onStart: () => void,
    ) {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Alt+V (Option+V on Mac) — toggle recording
            if (e.altKey && e.code === "KeyV" && !e.repeat) {
                e.preventDefault();

                if (isRecording.value) {
                    // Second press — stop recording
                    onStopCallback?.();
                } else if (canStart() && !isTranscribing.value) {
                    // First press — start recording
                    onStart();
                    startRecording();
                }
            }
        };

        // Suppress the √ character that Mac inserts for Option+V.
        // Even with preventDefault on keydown, some browsers still fire
        // a beforeinput/input event for dead-key sequences.
        const handleBeforeInput = (e: InputEvent) => {
            if (e.data === "√" && (isRecording.value || isTranscribing.value)) {
                e.preventDefault();
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        document.addEventListener("beforeinput", handleBeforeInput);

        keyboardCleanup = () => {
            document.removeEventListener("keydown", handleKeyDown);
            document.removeEventListener("beforeinput", handleBeforeInput);
        };
    }

    function unregisterKeyboardShortcut() {
        keyboardCleanup?.();
        keyboardCleanup = null;
    }

    async function startRecording() {
        error.value = null;
        liveTranscript.value = "";
        finalizedText = "";

        try {
            audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        } catch {
            error.value = "Microphone access denied. Please allow microphone access and try again.";
            return;
        }

        // Open WebSocket to our proxy
        const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
        ws = new WebSocket(`${protocol}//${window.location.host}/api/transcribe/stream`);

        ws.onopen = () => {
            if (!audioStream) return;

            const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
                ? "audio/webm;codecs=opus"
                : MediaRecorder.isTypeSupported("audio/webm")
                    ? "audio/webm"
                    : "";

            mediaRecorder = mimeType
                ? new MediaRecorder(audioStream, { mimeType })
                : new MediaRecorder(audioStream);

            mediaRecorder.ondataavailable = (e) => {
                if (e.data.size > 0 && ws?.readyState === WebSocket.OPEN) {
                    ws.send(e.data);
                }
            };

            mediaRecorder.start(250);
            isRecording.value = true;
            isTranscribing.value = true;
        };

        ws.onmessage = (event) => {
            try {
                const data: DeepgramResult = JSON.parse(event.data as string);
                const transcript = data.channel?.alternatives?.[0]?.transcript ?? "";

                if (data.is_final) {
                    if (transcript) {
                        finalizedText += (finalizedText ? " " : "") + transcript;
                    }
                    liveTranscript.value = finalizedText;
                } else {
                    // Show finalized + interim text
                    const interim = transcript ? (finalizedText ? " " : "") + transcript : "";
                    liveTranscript.value = finalizedText + interim;
                }
            } catch {
                // ignore malformed messages
            }
        };

        ws.onerror = () => {
            error.value = "Connection to transcription service failed";
            cleanup();
        };

        ws.onclose = () => {
            isTranscribing.value = false;
        };

        // Listen for mouseup/touchend on the entire document so cursor can move freely
        const handleGlobalUp = () => {
            if (isRecording.value) {
                onStopCallback?.();
            }
        };
        document.addEventListener("mouseup", handleGlobalUp);
        document.addEventListener("touchend", handleGlobalUp);
        cleanupDocListeners = () => {
            document.removeEventListener("mouseup", handleGlobalUp);
            document.removeEventListener("touchend", handleGlobalUp);
        };
    }

    function stopRecording() {
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
        }
        audioStream?.getTracks().forEach((t) => t.stop());
        audioStream = null;
        isRecording.value = false;

        // Close WebSocket — Deepgram will flush remaining results before closing
        if (ws && ws.readyState === WebSocket.OPEN) {
            ws.close();
        }

        cleanupDocListeners?.();
        cleanupDocListeners = null;
    }

    function cancelRecording() {
        liveTranscript.value = "";
        finalizedText = "";
        cleanup();
    }

    function cleanup() {
        if (mediaRecorder && mediaRecorder.state !== "inactive") {
            mediaRecorder.stop();
        }
        audioStream?.getTracks().forEach((t) => t.stop());
        audioStream = null;
        if (ws && ws.readyState !== WebSocket.CLOSED) {
            ws.close();
        }
        ws = null;
        isRecording.value = false;
        cleanupDocListeners?.();
        cleanupDocListeners = null;
    }

    return {
        isRecording: readonly(isRecording),
        isTranscribing: readonly(isTranscribing),
        error: readonly(error),
        liveTranscript: readonly(liveTranscript),
        startRecording,
        stopRecording,
        cancelRecording,
        registerStopCallback,
        registerKeyboardShortcut,
        unregisterKeyboardShortcut,
    };
}
